import { Banner, BlockStack, Box, Button, Card, InlineStack, Layout, Page, Pagination, Text } from '@shopify/polaris';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiErrorMessage } from '../../app/api/error';
import agent, { FitmentImportHistoryItem } from '../../app/api/agent';
import ImportHistoryCards from './ImportHistoryCards';

const pageSize = 20;

const ImportHistoryPage = () => {
    const navigate = useNavigate();
    const [history, setHistory] = useState<FitmentImportHistoryItem[]>([]);
    const [historyError, setHistoryError] = useState<string | undefined>();
    const [historyErrorTitle, setHistoryErrorTitle] = useState<string | undefined>();
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [archivingIds, setArchivingIds] = useState<Record<number, boolean>>({});
    const [downloadingIds, setDownloadingIds] = useState<Record<number, boolean>>({});
    const [downloadingReviewIds, setDownloadingReviewIds] = useState<Record<number, boolean>>({});
    const [page, setPage] = useState(1);

    const loadHistory = useCallback(async () => {
        try {
            setIsLoadingHistory(true);
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            const result = await agent.Imports.history();
            setHistory(result);
        } catch (error: any) {
            setHistoryError(await getApiErrorMessage(error, 'Failed to load import history.'));
        } finally {
            setIsLoadingHistory(false);
        }
    }, []);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    const totalPages = Math.max(1, Math.ceil(history.length / pageSize));

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    const currentItems = useMemo(() => {
        const startIndex = (page - 1) * pageSize;
        return history.slice(startIndex, startIndex + pageSize);
    }, [history, page]);

    const handleArchive = useCallback(async (queueId: number) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setArchivingIds((prev) => ({ ...prev, [queueId]: true }));
            await agent.Imports.archive(queueId);
            setHistory((prev) => prev.filter((item) => item.queueId !== queueId));
        } catch (error: any) {
            setHistoryErrorTitle('Unable to archive history item');
            setHistoryError(await getApiErrorMessage(error, 'Failed to archive import history item.'));
        } finally {
            setArchivingIds((prev) => ({ ...prev, [queueId]: false }));
        }
    }, []);

    const handleDownload = useCallback(async (item: FitmentImportHistoryItem) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setDownloadingIds((prev) => ({ ...prev, [item.queueId]: true }));
            const response = await agent.Imports.download(item.queueId);
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = item.originalFileName || `import-${item.queueId}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error: any) {
            setHistoryErrorTitle('Unable to download import file');
            setHistoryError(await getApiErrorMessage(error, 'Failed to download import file.'));
        } finally {
            setDownloadingIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    }, []);

    const handleDownloadReview = useCallback(async (item: FitmentImportHistoryItem) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setDownloadingReviewIds((prev) => ({ ...prev, [item.queueId]: true }));
            const response = await agent.Imports.downloadReview(item.queueId);
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            const baseName = (item.originalFileName || `import-${item.queueId}`).replace(/\.[^/.]+$/, '');
            link.href = blobUrl;
            link.download = `${baseName}-review.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error: any) {
            setHistoryErrorTitle('Unable to download review file');
            setHistoryError(await getApiErrorMessage(error, 'Failed to download import review file.'));
        } finally {
            setDownloadingReviewIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    }, []);

    return (
        <Page
            title="Import history"
            backAction={{ content: 'Imports', onAction: () => navigate('/imports') }}
        >
            <Layout>
                <Layout.Section>
                    <Card>
                        <BlockStack gap="300">
                            <InlineStack align="space-between">
                                <Text as="h2" variant="headingMd">All imports</Text>
                                <Button onClick={loadHistory} loading={isLoadingHistory}>Refresh</Button>
                            </InlineStack>

                            {historyError && (
                                <Banner tone="critical" title={historyErrorTitle || 'Unable to load import history'}>
                                    <p>{historyError}</p>
                                </Banner>
                            )}

                            {history.length === 0 && !isLoadingHistory ? (
                                <Text as="p" tone="subdued">No import history yet.</Text>
                            ) : (
                                <>
                                    <ImportHistoryCards
                                        items={currentItems}
                                        archivingIds={archivingIds}
                                        downloadingIds={downloadingIds}
                                        downloadingReviewIds={downloadingReviewIds}
                                        onArchive={handleArchive}
                                        onDownload={handleDownload}
                                        onDownloadReview={handleDownloadReview}
                                    />
                                    <Box paddingBlockStart="200">
                                        <InlineStack align="center">
                                            <Pagination
                                                hasPrevious={page > 1}
                                                onPrevious={() => setPage((prev) => Math.max(1, prev - 1))}
                                                hasNext={page < totalPages}
                                                onNext={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                                            />
                                        </InlineStack>
                                    </Box>
                                </>
                            )}
                        </BlockStack>
                    </Card>
                </Layout.Section>
            </Layout>
        </Page>
    );
};

export default ImportHistoryPage;
